import React, {useEffect, useMemo, useState} from "react";
import {Select, Spin} from "antd";
import debounce from "lodash/debounce";
import RenderIf from "@/components/render-if";

const { Option } = Select;

const TaggableSelect = ({
    name,
    label,
    searchFn,
    render,
    onChange,
    values = [],
    className,
    taggableType = "multiple",
    maxCount = 10000,
}) => {
    const [searchTerm, setSearchTerm] = useState("");

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                setSearchTerm(value);
            }, 500),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const {
        data: options = [],
        isLoading,
        isRefetching,
    } = searchFn(
        { search: searchTerm },
        { enabled: !!searchTerm }
    );

    const {
        data: savedOptions = [],
        isLoading: isLoadingSavedOptions,
    } = searchFn(
        { ids: values.length ? values.join(",") : "" },
        { enabled: values.length > 0 }
    );

    const searchResults = options?.data?.data || [];
    const selectedResults = savedOptions?.data?.data || [];

    /**
     * When searching, only show search results.
     * Otherwise, show the currently selected options.
     */
    const mergedOptions = searchTerm
        ? searchResults
        : selectedResults;

    const loading =
        isLoading ||
        isRefetching ||
        isLoadingSavedOptions;

    return (
        <div className={className}>
            <label htmlFor={`select_${name}`}>
                {label}
            </label>

            <RenderIf condition={isLoadingSavedOptions}>
                <Spin
                    className="mx-2"
                    size="small"
                />
            </RenderIf>

            <Select
                id={`select_${name}`}
                mode={taggableType}
                maxCount={maxCount}
                style={{ width: "100%" }}
                placeholder="جستجو کنید ..."
                value={values}
                loading={loading}
                showSearch
                allowClear
                filterOption={false}
                onChange={onChange}
                onSearch={debouncedSearch}
                notFoundContent={
                    loading ? (
                        <Spin size="small" />
                    ) : null
                }
            >
                {mergedOptions.map((option) => (
                    <Option
                        key={`select-option-${name}-${option.id}`}
                        value={option.id}
                    >
                        {typeof render === "function"
                            ? render(option)
                            : option?.title}
                    </Option>
                ))}
            </Select>
        </div>
    );
};

export default TaggableSelect;
